import React from 'react';
import styled from 'styled-components';
import { LabelUppercase, PSmall } from '../elements/Text';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Button, { ButtonProps } from '../elements/Button';
import { useTheme } from 'styled-components';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.dimensions.sectionBorderRadius};
  overflow: hidden;
`;

const HeaderStyled = styled.div`
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  padding: 20px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgDefault};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleIcon = styled.i<{ expanded: boolean }>`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: transform 0.3s ease-in-out;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

export interface CollapsibleSectionProps {
  headerLeft: React.ReactNode;
  headerRight: React.ReactNode;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
}

export const CollapsibleSectionHeader = styled(PSmall)`
  margin: 0;
  padding: 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textActive};
`;

const CollapsibleSection = ({
  headerLeft,
  headerRight,
  children,
  expanded,
  onToggle,
}: CollapsibleSectionProps) => {
  return (
    <Section>
      <HeaderStyled onClick={onToggle} aria-expanded={expanded}>
        <HeaderLeft>
          <ToggleIcon
            className={
              expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
            }
            expanded={expanded}
          />
          {headerLeft}
        </HeaderLeft>
        {headerRight}
      </HeaderStyled>
      <CollapsibleSectionContent isExpanded={expanded}>
        {children}
      </CollapsibleSectionContent>
    </Section>
  );
};
export const CollapsibleSectionLabel = styled(LabelUppercase)`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  white-space: nowrap;
`;

export const CollapsibleSectionContent = ({
  children,
  isExpanded,
}: {
  children: React.ReactNode;
  isExpanded: boolean;
}) => {
  const [animationParent] = useAutoAnimate<HTMLDivElement>();
  return <div ref={animationParent}>{isExpanded && children}</div>;
};

export const CollapsibleSectionFooter = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  gap: 16px;
  overflow-x: auto;
  padding: 10px 16px;
  text-align: right;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledButton = styled(Button)`
  padding: 8px 12px;
  font-size: 14px;
  margin-top: 0;
`;

export const CollapsibleSectionFooterButton = ({
  label,
  onClick,
  faIcon,
  ...rest
}: {
  label: string;
  onClick: () => void;
  faIcon: string;
} & ButtonProps) => {
  const theme = useTheme();
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <StyledButton
        label={label}
        onClick={onClick}
        faIcon={faIcon}
        color={theme.colors.textPrimary}
        type="secondary"
        {...rest}
      />
    </div>
  );
};

export default CollapsibleSection;
